/**
 * SkillForm Component
 * Form for creating and editing skills
 * Matches admin interface fields: skill_id, name, description, command, display_order, enabled
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import type { Skill, CreateSkillRequest, UpdateSkillRequest } from '@/types/skills';

interface SkillFormProps {
  skill?: Skill | null;
  onSubmit: (data: CreateSkillRequest | UpdateSkillRequest) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

interface FormData {
  skill_id: string;
  name: string;
  description: string;
  command: string;
  display_order: number;
  enabled: boolean;
}

export const SkillForm: React.FC<SkillFormProps> = ({
  skill,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const isEditing = !!skill;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      skill_id: skill?.skill_id || '',
      name: skill?.name || '',
      description: skill?.description || '',
      command: skill?.command || '',
      display_order: skill?.display_order ?? 0,
      enabled: skill?.enabled ?? true,
    },
  });

  useEffect(() => {
    reset({
      skill_id: skill?.skill_id || '',
      name: skill?.name || '',
      description: skill?.description || '',
      command: skill?.command || '',
      display_order: skill?.display_order ?? 0,
      enabled: skill?.enabled ?? true,
    });
  }, [skill, reset]);

  const handleFormSubmit = (data: FormData) => {
    // For update, don't include skill_id since it can't be changed
    if (isEditing) {
      const { skill_id: _, ...updateData } = data;
      onSubmit(updateData);
    } else {
      onSubmit(data);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* Skill ID - required for create, readonly for edit */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Skill ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          placeholder="e.g., lucky_charm"
          disabled={isEditing}
          className={`
            w-full px-3 py-2 border rounded-lg text-sm transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${isEditing ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}
            ${
              errors.skill_id
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }
          `}
          {...register('skill_id', {
            required: !isEditing ? 'Skill ID is required' : false,
            pattern: {
              value: /^[a-zA-Z0-9_-]+$/,
              message: 'Only alphanumeric, underscore, and hyphen allowed',
            },
          })}
        />
        <p className="mt-1 text-xs text-gray-500">
          {isEditing ? 'Cannot be changed after creation' : 'Unique identifier, cannot be changed later'}
        </p>
        {errors.skill_id && (
          <p className="mt-1 text-sm text-red-600">{errors.skill_id.message}</p>
        )}
      </div>

      <Input
        label="Name"
        placeholder="Enter skill display name"
        error={errors.name?.message}
        required
        {...register('name', {
          required: 'Name is required',
          minLength: {
            value: 1,
            message: 'Name is required',
          },
        })}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          placeholder="Brief description of the skill's functionality"
          className={`
            w-full px-3 py-2 border rounded-lg text-sm transition-colors resize-none h-20
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${
              errors.description
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }
          `}
          {...register('description')}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Command <span className="text-red-500">*</span>
        </label>
        <textarea
          placeholder="Trigger command text sent when user clicks the skill"
          className={`
            w-full px-3 py-2 border rounded-lg text-sm transition-colors resize-none h-20
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${
              errors.command
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }
          `}
          {...register('command', {
            required: 'Command is required',
          })}
        />
        <p className="mt-1 text-xs text-gray-500">
          Message content sent when user clicks this skill
        </p>
        {errors.command && (
          <p className="mt-1 text-sm text-red-600">{errors.command.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Display Order
        </label>
        <input
          type="number"
          min="0"
          placeholder="0"
          className={`
            w-full px-3 py-2 border rounded-lg text-sm transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-0
            ${
              errors.display_order
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }
          `}
          {...register('display_order', {
            valueAsNumber: true,
            min: { value: 0, message: 'Must be 0 or greater' },
          })}
        />
        <p className="mt-1 text-xs text-gray-500">Lower number = higher priority</p>
        {errors.display_order && (
          <p className="mt-1 text-sm text-red-600">{errors.display_order.message}</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="enabled"
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          {...register('enabled')}
        />
        <label htmlFor="enabled" className="text-sm text-gray-700">
          Enable this skill
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="secondary" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" loading={isSubmitting}>
          {skill ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
};
